import { billingDetailPages, pagesConfig } from '../../shared/pages-config';
import { Link, Outlet } from 'react-router-dom';
import { useRouteMatch } from '../../shared/useRouteMatch';
import { useUserSettings } from '../../utils/use-user-settings';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';
import SimpleBreadcrumbsPage from '../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../shared/analytics/module-list';
import { Messages } from '../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import SharedTabCard from '../../shared/shared-tab-card';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import MuiLink from '@mui/material/Link';
import SharedTabContentCard from '../../shared/shared-tab-content-card';

export default function BillingPage() {
  const { usersTier, billingRoles, isDefault } = useUserSettings();

  const routeMatch = useRouteMatch([
    `${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}`,
    `${pagesConfig.billing.link}/${pagesConfig.billingPayments.link}`,
    `${pagesConfig.billing.link}/${pagesConfig.billingContacts.link}`,
  ]);

  const isFormPage = useRouteMatch([
    `${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}/${pagesConfig.addLicense.link}`,
  ]);

  if (isFormPage) return <Outlet />;

  const currentTab =
    routeMatch?.pattern?.path ?? `${pagesConfig.billingSubscriptions.link}`;

  if (usersTier.value === 'trial') {
    return (
      <Container maxWidth={'xl'} sx={{ width: '75vw', minWidth: 600 }}>
        <Stack
          sx={{ mb: 2, flexDirection: 'row', alignItems: 'center' }}
          gap={0.5}
        >
          <Typography sx={{ fontWeight: 300, fontSize: 32 }}>
            {pagesConfig.billing.name}
          </Typography>
        </Stack>
        <Alert severity="info" sx={{ mt: 1, width: 'fit-content' }}>
          We see you are on a gPanel trial. Please contact your sales
          representative to purchase, or email{' '}
          <MuiLink href="mailto:sales@promevo.com">sales@promevo.com</MuiLink>.
        </Alert>
      </Container>
    );
  }
  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[{ label: ModuleList.modules.billing.name }]}
      bookmarkKey={pagesConfig.billing.value}
      shouldAllowHeightToScroll
      isSubTabs
    >
      <Box>
        <SharedTabCard>
          <Tabs
            value={currentTab.split('/').pop()}
            aria-label="Billing"
            sx={{
              mx: 1,
              '& .MuiTabs-flexContainer': {
                marginTop: '6px',
              },
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {billingDetailPages.map((page) => {
              const doesntHavePermission =
                !isAbleToSeeUIElement(billingRoles, page.requiredPermission) &&
                page.requiredPermission !== '*';
              const doesntHaveTier = usersTier.rank < page.minimumTier;
              const shouldShowButDisabled = isDefault && doesntHaveTier;
              if (shouldShowButDisabled) {
                return (
                  <Tab
                    key={page.name}
                    value={page.link}
                    component={Link}
                    to={page.link}
                    label={
                      <Tooltip
                        title={Messages.Errors.Tier.Viewing(
                          page.minimumTierDisplayValue
                        )}
                        onClick={(e) => e.preventDefault()}
                        arrow
                      >
                        <span style={{ pointerEvents: 'auto' }}>
                          {page.name}
                        </span>
                      </Tooltip>
                    }
                    icon={<page.icon />}
                    iconPosition="start"
                    sx={{
                      minHeight: '40px',
                      height: '40px',
                      width: 'fit-content',
                    }}
                    disabled
                  />
                );
              }
              return (
                !(doesntHavePermission || doesntHaveTier) && (
                  <Tab
                    key={page.name}
                    value={page.link}
                    component={Link}
                    to={page.link}
                    label={page.name}
                    icon={<page.icon />}
                    iconPosition="start"
                    sx={{
                      minHeight: '40px',
                      height: '40px',
                      width: 'fit-content',
                    }}
                  />
                )
              );
            })}
          </Tabs>
        </SharedTabCard>

        <SharedTabContentCard
          sx={{
            minHeight: 420,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box
              sx={{
                flexGrow: 1,
              }}
            >
              <Outlet />
            </Box>
          </Stack>
        </SharedTabContentCard>
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
